import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { useAppSelector } from "../../app/hooks";
import { RouteId } from "../../generated/gpb/api/id";
import { filterRawRouteResults } from "../../components/trips/tripUtils";
import {
    RouteRequestInputType,
    RouteRequestResult,
    RouteRequestState,
    TimeOfDay,
    Trip,
    Waypoint
} from "../../generated/gpb/api/route";
import { createNewWaypoint, defaultWaypoint } from "../../components/trips/form/TripFormTypeConversions";
import { isValidLocation, isValidPlacename } from "../../components/location/LocationSelectModal";
import { MAX_WAYPOINTS } from "../../components/UserVisibleStrings";
import { getRouteBoundingBox } from "../../components/risk/ThumbnailMapUtils";

// Define an enum for RiskViewer states
export enum RiskViewerKind
    {
    TRIP_STATE = "tripState",
    WILMA_STATE = "wilmaState",
    SELECTION_STATE = "selectionState",
}

export enum RouteViewState
    {
    COLORED_ROUTE = 0,
    ROUTE_WILMA = 1,
}

export const MAX_ROUTES_TO_DISPLAY = 3;

/**
 * We encode some additional information (not in the GPB sent to the backend) for use by the trip input form. So this
 * expanded waypoint type has additional info (as well as some optional fields being made required to match the zod expectations.
 */
export type WaypointExpanded = {
    location: {
        location: {
            latitude: number,
            longitude: number,
        },
        locationName: string,
        valid: boolean,
    },
    overnightStop?: { minNights: number, maxNights: number },
    pitStop?: { minDurationMinutes: number, maxDurationMinutes: number },
    startTime: TimeOfDay | undefined,
    firstDayAfterWaypointDesiredEndOverride: TimeOfDay | undefined,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isExpandedWaypoint( wp: any ): wp is WaypointExpanded
{
    return "valid" in wp;
}

export function convertWaypointToExpanded( wp: Waypoint | WaypointExpanded ): WaypointExpanded
{
    if ( isExpandedWaypoint( wp ) ) return wp as WaypointExpanded;
    return {
        ...wp,
        location:
            {
                location: wp.location?.location ?? { latitude: 0, longitude: 0 },
                locationName: wp.location?.locationName ?? "",
                valid: true
            },
        overnightStop: wp.overnightStop === undefined ? undefined : {
            minNights: wp.overnightStop.minNights !== undefined ? wp.overnightStop.minNights : 1,
            maxNights: wp.overnightStop.maxNights !== undefined ? wp.overnightStop.maxNights : 1
        },
        pitStop: wp.pitStop === undefined ? undefined : {
            minDurationMinutes: wp.pitStop.minDurationMinutes ?? 0,
            maxDurationMinutes: wp.pitStop.maxDurationMinutes ?? 0
        },
        startTime: wp.startTime !== undefined ? wp.startTime : undefined,
        firstDayAfterWaypointDesiredEndOverride: undefined
    };
}

// Define a type for the RiskViewerState
export type RiskViewerState =
    | {
        readonly kind: RiskViewerKind.TRIP_STATE,
        tripState: Trip | undefined,
        routesToDisplay: Array<RouteRequestResult | undefined> | undefined,
        selectedRoute: RouteId | undefined,
        homeViewState: Array<Array<number>> | undefined,
        /**
         * We need a way to track the state of whether or not the user clicks to choose the sureroute option (vs choosing to initiate
         * multistart).  The reason is that we only query the trips database content when the trips drawer is open, so when we change
         * the state for mulstart_eligible to another state, we won't see it unless we close the card and reopen.  Therefore, this local
         * state gives us a quick way to see if the button is clicked by initializing based on the RouteRequestState.
         */
        hasSelectedBackendRoute: boolean,
        routeViewState: RouteViewState,
    }
    | { readonly kind: RiskViewerKind.WILMA_STATE }
    | {
        readonly kind: RiskViewerKind.SELECTION_STATE,
        waypoints: Array<WaypointExpanded> | undefined,
        homeViewState: Array<Array<number>> | undefined,
    };

// Define the initial state using that type
const initialRiskViewerState: RiskViewerState = {
    kind: RiskViewerKind.WILMA_STATE,
};
type ClickWaypointPayload = {
    location: { lng: number, lat: number },
    placeName: string,
    index: number | undefined,
};
type UpdateWaypointPayload = {
    index: number,
    waypoint: WaypointExpanded | Waypoint,
    isDrag: boolean,
};
type RoutePayload = {
    routes: Array<RouteRequestResult>,
    newHomeViewState: Array<Array<number>> | undefined,
};
type SetSelectionPayload = {
    waypoints: Array<Waypoint>,
    homeViewState: Array<Array<number>> | undefined,
};

function insertWaypointAtIndex( state: RiskViewerState, index: number, waypoint: WaypointExpanded )
{
    if ( state.kind === RiskViewerKind.SELECTION_STATE )
    {
        if ( state.waypoints === undefined )
        {
            state.waypoints = [];
        }
        while ( state.waypoints.length <= index )
        {
            state.waypoints.push( defaultWaypoint );
        }
        state.waypoints[index] = waypoint;
        while ( state.waypoints.length < 2 )
        {
            state.waypoints.push( defaultWaypoint );
        }
    }
}

export const riskViewerSlice = createSlice( {
    name: "riskViewer",
    initialState: initialRiskViewerState as RiskViewerState,
    reducers: {
        /**
         * Sets the state to tripState and updates its properties.
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new trip state.
         */
        setTripState(
            state,
            action: PayloadAction<{
                tripState: Trip | undefined,
                routesToDisplay: Array<RouteRequestResult> | undefined,
                selectedRoute: RouteId | undefined,
                homeViewState: Array<Array<number>>,
            }>
        )
        {
            state.kind = RiskViewerKind.TRIP_STATE;
            if ( state.kind === RiskViewerKind.TRIP_STATE )
            {
                state.tripState = action.payload.tripState;
                state.routesToDisplay = action.payload.routesToDisplay;
                state.selectedRoute = action.payload.selectedRoute;
                state.homeViewState = action.payload.homeViewState;
                state.hasSelectedBackendRoute = false;
                if ( action.payload.tripState?.routeRequestInput?.requestType === RouteRequestInputType.ADVISORY )
                {
                    state.routeViewState = RouteViewState.COLORED_ROUTE;
                }
                else
                {
                    state.routeViewState = RouteViewState.ROUTE_WILMA;
                }
            }
        },

        selectMultistartRoute( state, action: PayloadAction<RouteId | undefined> )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE && state.routesToDisplay !== undefined )
            {
                //filter deselected routes
                const nonUndefinedRoutes = state.routesToDisplay.filter( ( route ): route is RouteRequestResult => route !== undefined );
                const newRoutesToDisplay = filterRawRouteResults(
                    nonUndefinedRoutes,
                    action.payload,
                    false,
                ).map( each => each.element );
                state.selectedRoute = action.payload;
                //change the trip routeState
                const newtrip = {
                    ...state.tripState,
                    routeState: RouteRequestState.ROUTE_SELECTED
                };

                return {
                    ...state,
                    routesToDisplay: newRoutesToDisplay,
                    tripState: newtrip,
                } as RiskViewerState;
            }

            return state;
        },

        /**
         * Sets the routes to display if the state is already tripState
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new routesToDisplay.
         */
        setRoutesToDisplay( state: RiskViewerState, action: PayloadAction<RoutePayload> )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE )
            {
                state.routesToDisplay = action.payload.routes;
                if ( action.payload.routes.length > 0 )
                {
                    state.selectedRoute = action.payload.routes[0].routeId;
                }
                if ( action.payload.newHomeViewState )
                {
                    state.homeViewState = action.payload.newHomeViewState;
                }
            }
        },

        /**
         * Sets the routes to display if the state is already tripState. Does not update the route request index or
         * view state as this method is only used for admins to view not-selected multistart variations
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new routesToDisplay.
         */
        adminOverrideRoutesToDisplay( state: RiskViewerState, action: PayloadAction<Array<RouteRequestResult>> )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE )
            {
                state.routesToDisplay = action.payload;
                if ( action.payload.length > 0 )
                {
                    //Pick a state to display to the user that they can change later.
                    state.selectedRoute = action.payload[0].routeId;
                }
            }
        },

        /**
         * Sets the selected route if the state is already tripState.
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new selectedRoute.
         */
        setSelectedRoute( state: RiskViewerState, action: PayloadAction<RouteId | undefined> )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE )
            {
                const routeIndex = state.routesToDisplay?.findIndex( route => route?.routeId?.hexString === action.payload?.hexString );

                if ( action.payload === undefined || routeIndex !== -1 )
                {
                    state.selectedRoute = action.payload;
                }
            }
        },

        /**
         * Filters deselected routes out of the routes to display
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new selectedRoute.
         */
        filterDeselectedRoutes( state: RiskViewerState, action: PayloadAction<RouteId | undefined> )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE && state.routesToDisplay !== undefined )
            {
                const nonUndefinedRoutes = state.routesToDisplay.filter( ( route ): route is RouteRequestResult => route !== undefined );
                {
                    state.routesToDisplay = filterRawRouteResults(
                        nonUndefinedRoutes,
                        action.payload,
                        false,
                    ).map( each => each.element );
                }
                state.selectedRoute = action.payload;
            }
        },

        /**
         * Sets the flag indicating that the user has selected a route on the backend, but it has not propogated yet.
         * @param state - The current RiskViewerState.
         */
        setHasSelectedBackendRoute( state )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE )
            {
                state.hasSelectedBackendRoute = true;
            }
        },


        /**
         * Sets the state to wilmaState.
         * @param state - The current RiskViewerState.
         */
        setWilmaState( state )
        {
            state.kind = RiskViewerKind.WILMA_STATE;
        },

        /**
         * Sets the state to selectionState and updates its properties.
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new waypoints.
         */
        setSelectionState( state, action: PayloadAction<SetSelectionPayload> )
        {
            state.kind = RiskViewerKind.SELECTION_STATE;
            const expanded: Array<WaypointExpanded> = [];
            action.payload.waypoints.forEach( each =>
            {
                expanded.push( convertWaypointToExpanded( each ) );
            } );
            while ( expanded.length < 2 )
            {
                expanded.push( defaultWaypoint );
            }
            if ( state.kind === RiskViewerKind.SELECTION_STATE )
            {
                state.waypoints = expanded;
                state.homeViewState = action.payload.homeViewState;
            }
        },
        clickOnMap( state, action: PayloadAction<ClickWaypointPayload> )
        {
            if ( state.kind === RiskViewerKind.SELECTION_STATE &&
                isValidPlacename( action.payload.placeName ) &&
                isValidLocation( action.payload.location.lat, action.payload.location.lng ) &&
                state.waypoints && state.waypoints.length < MAX_WAYPOINTS - 1 )
            {
                const loc = action.payload.location;

                let index: number | undefined = action.payload.index;
                if ( index === undefined )
                {
                    if ( state.waypoints.includes( defaultWaypoint ) )
                    {
                        index = state.waypoints.findIndex( ( each ) => each === defaultWaypoint );
                    }
                    if ( index === undefined || index < 0 )
                    {
                        index = state.waypoints.length;
                    }
                }
                const wp: WaypointExpanded = index === undefined || index >= state.waypoints.length ? defaultWaypoint : state.waypoints[index];
                const updatedLocation = {
                    location: {
                        latitude: loc.lat,
                        longitude: loc.lng,
                    },
                    locationName: action.payload.placeName,
                    valid: isValidPlacename( action.payload.placeName ),
                };
                insertWaypointAtIndex( state, index, { ...wp, location: updatedLocation } );
            }
        },
        /**
         * Update a specific waypoint in the waypoint list.
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the new waypoint and its index in the waypoint list.
         */
        updateWaypoint( state, action: PayloadAction<UpdateWaypointPayload> )
        {
            insertWaypointAtIndex( state, action.payload.index, convertWaypointToExpanded( action.payload.waypoint ) );
            if (
                state.kind === RiskViewerKind.SELECTION_STATE &&
                !action.payload.isDrag &&
                state.waypoints &&
                state.waypoints.every(
                    ( wp ) => wp.location.location.latitude !== 0 || wp.location.location.latitude !== 0
                )
            )
            {
                state.homeViewState = getRouteBoundingBox( state.waypoints );
            }

        },

        /**
         * Remove a waypoint from the waypoint list based on the given index.
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing the index of the waypoint to be removed.
         */
        removeWaypoint( state, action: PayloadAction<{ index: number }> )
        {
            if ( state.kind === RiskViewerKind.SELECTION_STATE )
            {
                const { index } = action.payload;

                if ( state.waypoints && state.waypoints.length > index )
                {
                    state.waypoints.splice( index, 1 );
                }
            }
        },

        /**
         * reorders a waypoint in the selectionState
         * @param state - The current RiskViewerState.
         * @param action - The PayloadAction containing indices of the waypoints to swap and a flag to reset overnight stops.
         */
        dragWaypoint( state, action: PayloadAction<{ sourceIndex: number, destinationIndex: number }> )
        {
            if ( state.kind === RiskViewerKind.SELECTION_STATE && state.waypoints )
            {
                const { sourceIndex, destinationIndex } = action.payload;

                const waypointMoved = state.waypoints[sourceIndex];
                state.waypoints.splice( sourceIndex, 1 );
                state.waypoints.splice( destinationIndex, 0, waypointMoved );

                state.waypoints.map( ( waypoint, index ) =>
                {
                    if ( waypoint.overnightStop === undefined && waypoint.pitStop === undefined )
                    {
                        waypoint.overnightStop = { minNights: 1, maxNights: 1 };
                    }
                    if ( index !== 0 )
                    {
                        waypoint.startTime = undefined;
                    }
                } );

                const waypointStart = state.waypoints[0];
                const waypointDest = state.waypoints[state.waypoints.length - 1];
                state.waypoints[0] = createNewWaypoint( waypointStart as WaypointExpanded, false );
                state.waypoints[state.waypoints.length - 1] = createNewWaypoint( waypointDest as WaypointExpanded, false );
            }
        },

        /**
         * Sets the rout state if the state is already tripState.
         * @param state - The current RiskViewerState.
         */
        toggleRouteViewState( state: RiskViewerState )
        {
            if ( state.kind === RiskViewerKind.TRIP_STATE && state.routeViewState === RouteViewState.COLORED_ROUTE )
            {
                state.routeViewState = RouteViewState.ROUTE_WILMA;
            }
            else if ( state.kind === RiskViewerKind.TRIP_STATE && state.routeViewState === RouteViewState.ROUTE_WILMA )
            {
                state.routeViewState = RouteViewState.COLORED_ROUTE;
            }
        },

        /**
         * Resets the state to its initial value.
         */
        resetState()
        {
            return initialRiskViewerState;
        },
    },
} );

const selectRiskViewerState = ( state: RootState ) =>
{
    const { kind } = state.riskViewer;
    switch ( kind )
    {
        case RiskViewerKind.TRIP_STATE:
            return {
                kind,
                tripState: state.riskViewer.tripState,
                routesToDisplay: state.riskViewer.routesToDisplay,
                selectedRoute: state.riskViewer.selectedRoute,
                homeViewState: state.riskViewer.homeViewState,
                hasSelectedBackendRoute: state.riskViewer.hasSelectedBackendRoute,
                routeViewState: state.riskViewer.routeViewState,
            };
        case RiskViewerKind.WILMA_STATE:
            return { kind };
        case RiskViewerKind.SELECTION_STATE:
            return {
                kind,
                waypoints: state.riskViewer.waypoints,
                homeViewState: state.riskViewer.homeViewState
            };
        default:
            return state.riskViewer;
    }
};

const selectRoutesToDisplay = ( state: RootState ) =>
{
    if ( state.riskViewer.kind === RiskViewerKind.TRIP_STATE )
    {
        return state.riskViewer.routesToDisplay;
    }
    return undefined;
};

export const useRoutesToDisplay = () => useAppSelector( selectRoutesToDisplay );

export const useRiskViewerState = () => useAppSelector( selectRiskViewerState );
const selectRiskViewerKind = ( state: RootState ) => state.riskViewer.kind;
export const useRiskViewerStateKind = () => useAppSelector( selectRiskViewerKind );
const selectedRoute = ( state: RootState ) => state.riskViewer.kind === RiskViewerKind.TRIP_STATE ? state.riskViewer.selectedRoute : undefined;
export const useSelectedRoute = () => useAppSelector( selectedRoute );
const allRoutes = ( state: RootState ) => state.riskViewer.kind === RiskViewerKind.TRIP_STATE ? state.riskViewer.tripState?.routeResult : undefined;
export const useAllRoutes = () => useAppSelector( allRoutes );
const tripRouteState = ( state: RootState ) => state.riskViewer.kind === RiskViewerKind.TRIP_STATE ? state.riskViewer.tripState?.routeState : undefined;
export const useTripRouteState = () => useAppSelector( tripRouteState );
const hasSelectedBackendRuote = ( state: RootState ) => state.riskViewer.kind === RiskViewerKind.TRIP_STATE ? state.riskViewer.hasSelectedBackendRoute : false;
export const useHasSelectedBackendRoute = () => useAppSelector( hasSelectedBackendRuote );
const routeViewState = ( state: RootState ) => state.riskViewer.kind === RiskViewerKind.TRIP_STATE ? state.riskViewer.routeViewState : undefined;
export const useRouteViewState = () => useAppSelector( routeViewState );
const tripInputWaypoints = ( state: RootState ) => state.riskViewer.kind === RiskViewerKind.SELECTION_STATE ? state.riskViewer.waypoints : undefined;
export const useTripInputWaypoints = () => useAppSelector( tripInputWaypoints );

export const {
    setTripState,
    clickOnMap,
    setWilmaState,
    setSelectionState,
    setHasSelectedBackendRoute,
    updateWaypoint,
    dragWaypoint,
    setSelectedRoute,
    adminOverrideRoutesToDisplay,
    filterDeselectedRoutes,
    selectMultistartRoute,
    setRoutesToDisplay,
    resetState,
    toggleRouteViewState,
    removeWaypoint
} = riskViewerSlice.actions;
